import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../lib/api-config';
import {
  getDepartments, createDepartment, updateDepartment, deleteDepartment,
  getGLCodes, createGLCode, updateGLCode, deleteGLCode,
  getProjectManagers, assignProjectToManager, removeProjectAssignment, getManagerAssignments, getProjects,
  Department, GLCode, ProjectManager
} from '../../api/client';

function getAuthHeaders() {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}
import { ProjectRow } from '../../lib/types';

type TabType = 'departments' | 'gl-codes' | 'project-managers' | 'domain-owners' | 'user-roles';

interface Message {
  type: 'success' | 'error';
  text: string;
}

interface ManagerAssignment {
  [managerId: string]: string[];
}

const BUDGET_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

// Departments Tab Component
function DepartmentsTab() {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [message, setMessage] = useState<Message | null>(null);
  const [formData, setFormData] = useState({
    DepartmentName: '',
    DepartmentHead: '',
    Description: '',
    Budget: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [deptsRes, usersRes] = await Promise.all([
        getDepartments(),
        fetch(getApiUrl('/users'), { headers: getAuthHeaders() }).then(r => r.json()),
      ]);
      setDepartments(deptsRes.data || []);
      setUsers(usersRes.data || []);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load departments' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.DepartmentName.trim()) {
      setMessage({ type: 'error', text: 'Department name is required' });
      return;
    }

    try {
      const payload = {
        DepartmentName: formData.DepartmentName,
        DepartmentHead: formData.DepartmentHead || undefined,
        Description: formData.Description || undefined,
        Budget: formData.Budget ? parseInt(formData.Budget) : undefined,
      };

      if (editingId) {
        await updateDepartment(editingId, payload);
        setMessage({ type: 'success', text: 'Department updated successfully' });
      } else {
        await createDepartment(payload);
        setMessage({ type: 'success', text: 'Department created successfully' });
      }

      resetForm();
      await fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save department' });
    }
  };

  const handleEdit = (dept: Department) => {
    setEditingId(dept.DepartmentId);
    setFormData({
      DepartmentName: dept.DepartmentName,
      DepartmentHead: dept.DepartmentHead || '',
      Description: dept.Description || '',
      Budget: dept.Budget ? String(dept.Budget) : '',
    });
    setShowForm(true);
  };

  const handleDelete = async (departmentId: string) => {
    if (!confirm('Are you sure you want to delete this department?')) return;
    try {
      await deleteDepartment(departmentId);
      setMessage({ type: 'success', text: 'Department deleted successfully' });
      await fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete department' });
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      DepartmentName: '',
      DepartmentHead: '',
      Description: '',
      Budget: '',
    });
  };

  const getDepartmentHeadName = (email: string) => {
    const user = users.find(u => u.Email === email);
    return user ? `${user.Name} (${email})` : email;
  };

  const getMemberCount = (deptId: string) => {
    return departments.reduce((count, d) => (d.DepartmentId === deptId ? count + 1 : count), 0);
  };

  if (loading) return <div style={styles.container}><p>Loading departments...</p></div>;

  return (
    <div style={styles.tabContent}>
      {message && (
        <div style={{
          ...styles.message,
          ...(message.type === 'success' ? styles.successMessage : styles.errorMessage)
        }}>
          {message.text}
          <button onClick={() => setMessage(null)} style={styles.closeButton}>✕</button>
        </div>
      )}

      {!showForm && (
        <button onClick={() => setShowForm(true)} style={styles.addButton}>
          + Add Department
        </button>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Department Name *</label>
            <input
              type="text"
              value={formData.DepartmentName}
              onChange={(e) => setFormData({ ...formData, DepartmentName: e.target.value })}
              placeholder="e.g., Engineering"
              required
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Department Head</label>
            <select
              value={formData.DepartmentHead}
              onChange={(e) => setFormData({ ...formData, DepartmentHead: e.target.value })}
              style={styles.input}
            >
              <option value="">-- Select Department Head --</option>
              {users.map(user => (
                <option key={user.Email} value={user.Email}>
                  {user.Name} ({user.Email})
                </option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Description</label>
            <textarea
              value={formData.Description}
              onChange={(e) => setFormData({ ...formData, Description: e.target.value })}
              placeholder="Department description..."
              style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Annual Budget</label>
            <input
              type="number"
              value={formData.Budget}
              onChange={(e) => setFormData({ ...formData, Budget: e.target.value })}
              placeholder="0"
              min="0"
              style={styles.input}
            />
          </div>

          <div style={styles.buttonGroup}>
            <button type="submit" style={styles.submitButton}>
              {editingId ? 'Update Department' : 'Create Department'}
            </button>
            <button type="button" onClick={resetForm} style={styles.cancelButton}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeader}>
              <th style={styles.tableCell}>Department Name</th>
              <th style={styles.tableCell}>Department Head</th>
              <th style={styles.tableCell}>Budget</th>
              <th style={styles.tableCell}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {departments.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ ...styles.tableCell, textAlign: 'center', color: '#999' }}>
                  No departments. Create one to get started.
                </td>
              </tr>
            ) : (
              departments.map(dept => (
                <tr key={dept.DepartmentId} style={styles.tableRow}>
                  <td style={styles.tableCell}>{dept.DepartmentName}</td>
                  <td style={styles.tableCell}>
                    {dept.DepartmentHead ? getDepartmentHeadName(dept.DepartmentHead) : '—'}
                  </td>
                  <td style={styles.tableCell}>
                    {dept.Budget ? BUDGET_FORMATTER.format(dept.Budget) : '—'}
                  </td>
                  <td style={styles.tableCell}>
                    <div style={styles.actionButtons}>
                      <button
                        onClick={() => handleEdit(dept)}
                        style={styles.editButton}
                        title="Edit"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(dept.DepartmentId)}
                        style={styles.deleteButton}
                        title="Delete"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// GL Codes Tab Component
function GLCodesTab() {
  const [glCodes, setGLCodes] = useState<GLCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [message, setMessage] = useState<Message | null>(null);
  const [formData, setFormData] = useState({
    Code: '',
    Description: '',
    Category: '',
    Status: 'Active',
  });

  const categories = ['Labor', 'Materials', 'Equipment', 'Services', 'Other'];
  const statuses = ['Active', 'Inactive', 'Archived'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getGLCodes();
      setGLCodes(res.data || []);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load GL codes' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.Code.trim()) {
      setMessage({ type: 'error', text: 'GL Code is required' });
      return;
    }

    try {
      const payload = {
        Code: formData.Code,
        Description: formData.Description || undefined,
        Category: formData.Category || undefined,
        Status: formData.Status,
      };

      if (editingId) {
        await updateGLCode(editingId, payload);
        setMessage({ type: 'success', text: 'GL Code updated successfully' });
      } else {
        await createGLCode(payload);
        setMessage({ type: 'success', text: 'GL Code created successfully' });
      }

      resetForm();
      await fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save GL Code' });
    }
  };

  const handleEdit = (code: GLCode) => {
    setEditingId(code.GLCodeId);
    setFormData({
      Code: code.Code,
      Description: code.Description || '',
      Category: code.Category || '',
      Status: code.Status || 'Active',
    });
    setShowForm(true);
  };

  const handleDelete = async (glCodeId: string) => {
    if (!confirm('Are you sure you want to delete this GL Code?')) return;
    try {
      await deleteGLCode(glCodeId);
      setMessage({ type: 'success', text: 'GL Code deleted successfully' });
      await fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete GL Code' });
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      Code: '',
      Description: '',
      Category: '',
      Status: 'Active',
    });
  };

  if (loading) return <div style={styles.container}><p>Loading GL codes...</p></div>;

  return (
    <div style={styles.tabContent}>
      {message && (
        <div style={{
          ...styles.message,
          ...(message.type === 'success' ? styles.successMessage : styles.errorMessage)
        }}>
          {message.text}
          <button onClick={() => setMessage(null)} style={styles.closeButton}>✕</button>
        </div>
      )}

      {!showForm && (
        <button onClick={() => setShowForm(true)} style={styles.addButton}>
          + Add GL Code
        </button>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>GL Code *</label>
            <input
              type="text"
              value={formData.Code}
              onChange={(e) => setFormData({ ...formData, Code: e.target.value })}
              placeholder="e.g., 4100-010"
              required
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Description</label>
            <textarea
              value={formData.Description}
              onChange={(e) => setFormData({ ...formData, Description: e.target.value })}
              placeholder="GL code description..."
              style={{ ...styles.input, minHeight: '60px', resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Category</label>
              <select
                value={formData.Category}
                onChange={(e) => setFormData({ ...formData, Category: e.target.value })}
                style={styles.input}
              >
                <option value="">-- Select Category --</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Status</label>
              <select
                value={formData.Status}
                onChange={(e) => setFormData({ ...formData, Status: e.target.value })}
                style={styles.input}
              >
                {statuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={styles.buttonGroup}>
            <button type="submit" style={styles.submitButton}>
              {editingId ? 'Update GL Code' : 'Create GL Code'}
            </button>
            <button type="button" onClick={resetForm} style={styles.cancelButton}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeader}>
              <th style={styles.tableCell}>Code</th>
              <th style={styles.tableCell}>Description</th>
              <th style={styles.tableCell}>Category</th>
              <th style={styles.tableCell}>Status</th>
              <th style={styles.tableCell}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {glCodes.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ ...styles.tableCell, textAlign: 'center', color: '#999' }}>
                  No GL codes. Create one to get started.
                </td>
              </tr>
            ) : (
              glCodes.map(code => (
                <tr key={code.GLCodeId} style={styles.tableRow}>
                  <td style={styles.tableCell}><strong>{code.Code}</strong></td>
                  <td style={styles.tableCell}>{code.Description || '—'}</td>
                  <td style={styles.tableCell}>{code.Category || '—'}</td>
                  <td style={styles.tableCell}>
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '4px',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      backgroundColor: code.Status === 'Active' ? '#d4edda' : '#fff3cd',
                      color: code.Status === 'Active' ? '#155724' : '#856404',
                    }}>
                      {code.Status || 'Active'}
                    </span>
                  </td>
                  <td style={styles.tableCell}>
                    <div style={styles.actionButtons}>
                      <button
                        onClick={() => handleEdit(code)}
                        style={styles.editButton}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(code.GLCodeId)}
                        style={styles.deleteButton}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Project Managers Tab Component
function ProjectManagersTab() {
  const [managers, setManagers] = useState<ProjectManager[]>([]);
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);
  const [selectedManager, setSelectedManager] = useState('');
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [managerAssignments, setManagerAssignments] = useState<ManagerAssignment>({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [managersRes, projectsRes] = await Promise.all([
        getProjectManagers(),
        getProjects(),
      ]);
      setManagers(managersRes.data || []);
      setProjects(projectsRes.data || []);

      // Fetch assignments for each manager
      const assignments: ManagerAssignment = {};
      for (const manager of (managersRes.data || [])) {
        try {
          const assignRes = await getManagerAssignments(manager.UserId);
          assignments[manager.UserId] = assignRes.data?.map(a => a.ProjectId) || [];
        } catch {
          assignments[manager.UserId] = [];
        }
      }
      setManagerAssignments(assignments);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load project managers' });
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedManager || selectedProjects.length === 0) {
      setMessage({ type: 'error', text: 'Please select a manager and at least one project' });
      return;
    }

    try {
      await assignProjectToManager({
        managerId: selectedManager,
        projectIds: selectedProjects,
      });
      setMessage({ type: 'success', text: 'Projects assigned successfully' });
      setSelectedManager('');
      setSelectedProjects([]);
      setShowAssignForm(false);
      await fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to assign projects' });
    }
  };

  const handleRemoveAssignment = async (managerId: string, projectId: string) => {
    if (!confirm('Remove this project assignment?')) return;
    try {
      const assignmentId = `${managerId}-${projectId}`;
      await removeProjectAssignment(assignmentId);
      setMessage({ type: 'success', text: 'Assignment removed successfully' });
      await fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to remove assignment' });
    }
  };

  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.ProjectId === projectId);
    return project?.Title || 'Unknown Project';
  };

  if (loading) return <div style={styles.container}><p>Loading project managers...</p></div>;

  return (
    <div style={styles.tabContent}>
      {message && (
        <div style={{
          ...styles.message,
          ...(message.type === 'success' ? styles.successMessage : styles.errorMessage)
        }}>
          {message.text}
          <button onClick={() => setMessage(null)} style={styles.closeButton}>✕</button>
        </div>
      )}

      {!showAssignForm && (
        <button onClick={() => setShowAssignForm(true)} style={styles.addButton}>
          + Assign Projects
        </button>
      )}

      {showAssignForm && (
        <form onSubmit={handleAssign} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Project Manager *</label>
            <select
              value={selectedManager}
              onChange={(e) => {
                setSelectedManager(e.target.value);
                setSelectedProjects(managerAssignments[e.target.value] || []);
              }}
              required
              style={styles.input}
            >
              <option value="">-- Select Project Manager --</option>
              {managers.map(manager => (
                <option key={manager.UserId} value={manager.UserId}>
                  {manager.ManagerName} ({manager.Email})
                </option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Projects *</label>
            <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '0.5rem' }}>
              {projects.length === 0 ? (
                <p style={{ color: '#999', margin: 0 }}>No projects available</p>
              ) : (
                projects.map(project => (
                  <label key={project.ProjectId} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedProjects.includes(project.ProjectId)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedProjects([...selectedProjects, project.ProjectId]);
                        } else {
                          setSelectedProjects(selectedProjects.filter(id => id !== project.ProjectId));
                        }
                      }}
                    />
                    <span>{project.Title}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div style={styles.buttonGroup}>
            <button type="submit" style={styles.submitButton}>
              Assign Projects
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAssignForm(false);
                setSelectedManager('');
                setSelectedProjects([]);
              }}
              style={styles.cancelButton}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeader}>
              <th style={styles.tableCell}>Manager Name</th>
              <th style={styles.tableCell}>Email</th>
              <th style={styles.tableCell}>Department</th>
              <th style={styles.tableCell}>Assigned Projects</th>
              <th style={styles.tableCell}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {managers.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ ...styles.tableCell, textAlign: 'center', color: '#999' }}>
                  No project managers found.
                </td>
              </tr>
            ) : (
              managers.map(manager => {
                const assignments = managerAssignments[manager.UserId] || [];
                return (
                  <tr key={manager.UserId} style={styles.tableRow}>
                    <td style={styles.tableCell}>{manager.ManagerName}</td>
                    <td style={styles.tableCell}>{manager.Email}</td>
                    <td style={styles.tableCell}>{manager.Department || '—'}</td>
                    <td style={styles.tableCell}>
                      <div style={{ fontSize: '0.9rem' }}>
                        {assignments.length === 0 ? (
                          <span style={{ color: '#999' }}>No assignments</span>
                        ) : (
                          <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                            {assignments.map(projectId => (
                              <li key={projectId} style={{ marginBottom: '0.25rem' }}>
                                {getProjectName(projectId)}
                                <button
                                  onClick={() => handleRemoveAssignment(manager.UserId, projectId)}
                                  style={{
                                    marginLeft: '0.5rem',
                                    padding: '0',
                                    background: 'none',
                                    border: 'none',
                                    color: '#dc3545',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                  }}
                                  title="Remove assignment"
                                >
                                  [Remove]
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </td>
                    <td style={styles.tableCell}>
                      <button
                        onClick={() => {
                          setShowAssignForm(true);
                          setSelectedManager(manager.UserId);
                          setSelectedProjects(assignments);
                        }}
                        style={styles.editButton}
                      >
                        Manage
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Domain Owners Tab Component
function DomainOwnersTab() {
  const [domainOwnerships, setDomainOwnerships] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<Message | null>(null);
  const [formData, setFormData] = useState({
    userId: '',
    domain: '',
  });

  const DOMAINS = ['infrastructure', 'networking', 'voice', 'product', 'development'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [ownersRes, usersRes] = await Promise.all([
        fetch(getApiUrl('/domain-ownership'), { headers: getAuthHeaders() }).then(r => r.json()),
        fetch(getApiUrl('/users'), { headers: getAuthHeaders() }).then(r => r.json()),
      ]);
      setDomainOwnerships(ownersRes.data || []);
      setUsers(usersRes.data || []);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load domain ownership data' });
    } finally {
      setLoading(false);
    }
  };

  const handleAssignDomain = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.userId || !formData.domain) {
      setMessage({ type: 'error', text: 'Please select both user and domain' });
      return;
    }

    try {
      const response = await fetch(getApiUrl('/domain-ownership'), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: formData.userId,
          domain: formData.domain,
          assignedByEmail: localStorage.getItem('auth_email') || 'admin@example.com',
        }),
      });

      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: 'Domain ownership assigned successfully' });
        setFormData({ userId: '', domain: '' });
        await fetchData();
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to assign domain' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to assign domain' });
    }
  };

  const handleRemove = async (ownerId: string) => {
    if (!window.confirm('Are you sure you want to remove this domain ownership?')) return;

    try {
      const response = await fetch(`/domain-ownership/${ownerId}`, {
        method: 'DELETE',
      });

      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: 'Domain ownership removed successfully' });
        await fetchData();
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to remove domain' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to remove domain' });
    }
  };

  if (loading) return <div style={styles.tabContent}>Loading...</div>;

  return (
    <div style={styles.tabContent}>
      {message && (
        <div style={{
          ...styles.message,
          backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
          color: message.type === 'success' ? '#155724' : '#721c24',
        }}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleAssignDomain} style={{ marginBottom: '2rem' }}>
        <div style={styles.formGroup}>
          <label htmlFor="user-select" style={styles.label}>Select User</label>
          <select
            id="user-select"
            value={formData.userId}
            onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
            style={styles.input}
          >
            <option value="">-- Choose User --</option>
            {users.map(user => (
              <option key={user.UserId} value={user.UserId}>
                {user.Name} ({user.Email})
              </option>
            ))}
          </select>
        </div>

        <div style={styles.formGroup}>
          <label htmlFor="domain-select" style={styles.label}>Select Domain</label>
          <select
            id="domain-select"
            value={formData.domain}
            onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
            style={styles.input}
          >
            <option value="">-- Choose Domain --</option>
            {DOMAINS.map(domain => (
              <option key={domain} value={domain}>
                {domain.charAt(0).toUpperCase() + domain.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.buttonGroup}>
          <button type="submit" style={styles.submitButton}>
            Assign Domain
          </button>
        </div>
      </form>

      <h3>Current Domain Assignments</h3>
      {domainOwnerships.length === 0 ? (
        <p style={{ color: '#666' }}>No domain assignments yet.</p>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead style={styles.tableHeader}>
              <tr>
                <th style={styles.tableCell}>User</th>
                <th style={styles.tableCell}>Email</th>
                <th style={styles.tableCell}>Domain</th>
                <th style={styles.tableCell}>Assigned Date</th>
                <th style={styles.tableCell}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {domainOwnerships.map((ownership) => {
                const user = users.find(u => u.UserId === ownership.UserId);
                return (
                  <tr key={ownership.OwnerId} style={styles.tableRow}>
                    <td style={styles.tableCell}>{user?.Name || 'Unknown'}</td>
                    <td style={styles.tableCell}>{user?.Email || 'Unknown'}</td>
                    <td style={styles.tableCell}>
                      <span style={{
                        background: '#e3f2fd',
                        color: '#1565c0',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '3px',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                      }}>
                        {ownership.Domain}
                      </span>
                    </td>
                    <td style={styles.tableCell}>
                      {new Date(ownership.AssignedDate).toLocaleDateString()}
                    </td>
                    <td style={styles.tableCell}>
                      <div style={styles.actionButtons}>
                        <button
                          onClick={() => handleRemove(ownership.OwnerId)}
                          style={styles.deleteButton}
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// User Roles Tab Component
function UserRolesTab() {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserEmail, setSelectedUserEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [message, setMessage] = useState<Message | null>(null);
  const [validRoles, setValidRoles] = useState<string[]>([]);

  const VALID_ROLES = [
    'flow_Admin',
    'flow_ProjectManager',
    'flow_TeamMember',
    'flow_TeamLead',
    'flow_DepartmentLead',
    'flow_GovernanceCommittee',
    'flow_PublicUser',
    'flow_FunctionLead',
    'flow_DomainOwner',
    'flow_SuperUser',
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch('/users');
      const data = await response.json();
      if (data.success) {
        setUsers(data.data || []);
        setValidRoles(VALID_ROLES);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load users' });
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRole = async () => {
    if (!selectedUserEmail || !selectedRole) {
      setMessage({ type: 'error', text: 'Select a user and role' });
      return;
    }

    try {
      const response = await fetch(
        `/users/${encodeURIComponent(selectedUserEmail)}/role`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role: selectedRole }),
        }
      );

      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: `Role updated for ${selectedUserEmail}` });
        fetchUsers();
        setSelectedUserEmail('');
        setSelectedRole('');
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to update role' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update role' });
    }
  };

  if (loading) return <div style={styles.loadingText}>Loading users...</div>;

  return (
    <div style={styles.tabContent}>
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Assign Roles to Users</h3>

        {message && (
          <div style={{
            ...styles.message,
            ...(message.type === 'success' ? styles.successMessage : styles.errorMessage),
          }}>
            {message.text}
          </div>
        )}

        <div style={styles.formGrid}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Select User:</label>
            <select
              value={selectedUserEmail}
              onChange={(e) => setSelectedUserEmail(e.target.value)}
              style={styles.select}
            >
              <option value="">-- Choose a user --</option>
              {users.map((user) => (
                <option key={user.UserId} value={user.Email}>
                  {user.Name} ({user.Email}) - Current: {user.Role}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Select Role:</label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              style={styles.select}
            >
              <option value="">-- Choose a role --</option>
              {validRoles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleAssignRole}
            style={styles.saveButton}
          >
            Assign Role
          </button>
        </div>

        <div style={styles.usersTable}>
          <h4>Current Users & Roles</h4>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableRow}>
                <th style={styles.tableCell}>Email</th>
                <th style={styles.tableCell}>Name</th>
                <th style={styles.tableCell}>Current Role</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.UserId} style={styles.tableRow}>
                  <td style={styles.tableCell}>{user.Email}</td>
                  <td style={styles.tableCell}>{user.Name}</td>
                  <td style={styles.tableCell}>
                    <span style={{ ...styles.badge, background: '#e3f2fd', color: '#1976d2' }}>
                      {user.Role}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Main Admin Config Page
export default function AdminConfig() {
  // Detect which tab to show based on current path
  const currentPath = window.location.hash.startsWith('#/')
    ? window.location.hash.substring(2)
    : window.location.hash.substring(1);

  const getInitialTab = (): TabType => {
    if (currentPath === '/project-managers') return 'project-managers';
    if (currentPath === '/work-categories') return 'gl-codes';
    if (currentPath === '/strategic-goals') return 'domain-owners'; // Placeholder - may need its own tab
    return 'departments';
  };

  const [activeTab, setActiveTab] = useState<TabType>(getInitialTab());

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1>System Configuration</h1>
        <p style={styles.headerSubtitle}>Manage departments, GL codes, project manager assignments, and domain ownership</p>
      </div>

      <div style={styles.tabs}>
        {(['departments', 'gl-codes', 'project-managers', 'domain-owners', 'user-roles'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              ...styles.tabButton,
              ...(activeTab === tab ? styles.activeTab : styles.inactiveTab),
            }}
          >
            {tab === 'departments' && 'Departments'}
            {tab === 'gl-codes' && 'GL Codes'}
            {tab === 'project-managers' && 'Project Managers'}
            {tab === 'domain-owners' && 'Domain Owners'}
            {tab === 'user-roles' && 'User Roles'}
          </button>
        ))}
      </div>

      {activeTab === 'departments' && <DepartmentsTab />}
      {activeTab === 'gl-codes' && <GLCodesTab />}
      {activeTab === 'project-managers' && <ProjectManagersTab />}
      {activeTab === 'domain-owners' && <DomainOwnersTab />}
      {activeTab === 'user-roles' && <UserRolesTab />}
    </div>
  );
}

// Styles
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2rem',
    backgroundColor: '#f5f5f5',
    minHeight: '100vh',
  },
  header: {
    marginBottom: '2rem',
  },
  headerSubtitle: {
    color: '#666',
    fontSize: '1rem',
    marginTop: '0.5rem',
  },
  tabs: {
    display: 'flex',
    gap: '0',
    borderBottom: '2px solid #ddd',
    marginBottom: '2rem',
  },
  tabButton: {
    padding: '1rem 1.5rem',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 500,
    borderBottom: '3px solid transparent',
    marginBottom: '-2px',
    transition: 'all 0.2s ease',
  },
  activeTab: {
    borderColor: '#667eea',
    color: '#667eea',
  },
  inactiveTab: {
    color: '#666',
    borderColor: 'transparent',
  },
  tabContent: {
    backgroundColor: 'white',
    padding: '2rem',
    borderRadius: '4px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  message: {
    padding: '1rem',
    borderRadius: '4px',
    marginBottom: '1.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  successMessage: {
    backgroundColor: '#d4edda',
    color: '#155724',
    border: '1px solid #c3e6cb',
  },
  errorMessage: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    border: '1px solid #f5c6cb',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: 'currentColor',
    cursor: 'pointer',
    fontSize: '1.25rem',
    padding: '0',
    width: '1.5rem',
    height: '1.5rem',
  },
  addButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 500,
    marginBottom: '1.5rem',
    transition: 'background-color 0.2s ease',
  },
  form: {
    backgroundColor: '#f9f9f9',
    padding: '1.5rem',
    borderRadius: '4px',
    marginBottom: '2rem',
    border: '1px solid #e0e0e0',
  },
  formGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    fontWeight: 600,
    marginBottom: '0.5rem',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '1rem',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  buttonGroup: {
    display: 'flex',
    gap: '1rem',
    marginTop: '1.5rem',
  },
  submitButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 500,
    transition: 'background-color 0.2s ease',
  },
  cancelButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 5,
    transition: 'background-color 0.2s ease',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: 'white',
  },
  tableHeader: {
    backgroundColor: '#f9f9f9',
    borderBottom: '2px solid #ddd',
  },
  tableCell: {
    padding: '1rem',
    textAlign: 'left',
    borderBottom: '1px solid #eee',
  },
  tableRow: {
    transition: 'background-color 0.2s ease',
  },
  actionButtons: {
    display: 'flex',
    gap: '0.5rem',
  },
  editButton: {
    padding: '0.4rem 0.8rem',
    backgroundColor: '#ffc107',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 500,
  },
  deleteButton: {
    padding: '0.4rem 0.8rem',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 500,
  },
  section: {
    marginBottom: '2rem',
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginBottom: '1.5rem',
    color: '#333',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1rem',
    marginBottom: '2rem',
    alignItems: 'flex-end',
  },
  select: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '1rem',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  saveButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 500,
    transition: 'background-color 0.2s ease',
  },
  usersTable: {
    marginTop: '2rem',
  },
  badge: {
    display: 'inline-block',
    padding: '0.25rem 0.75rem',
    borderRadius: '12px',
    fontSize: '0.85rem',
    fontWeight: 500,
  },
  loadingText: {
    padding: '2rem',
    textAlign: 'center' as const,
    color: '#666',
  },
};
